import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import { motion, useInView } from "framer-motion";
import { useRef, useState, useEffect } from "react";

function Counter({ target }) {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true });
  const [count, setCount] = useState(0);

  const match = String(target).match(/^(\D*)(\d+)(\D*)$/) || ["", "", "0", ""];
  const prefix = match[1] || "";
  const numberStr = match[2];
  const suffix = match[3] || "";
  const end = parseInt(numberStr, 10);

  useEffect(() => {
    if (!isInView) return;
    let current = 0;
    const duration = 2000;
    const step = (end / duration) * 16;
    const tick = () => {
      current += step;
      if (current < end) {
        setCount(Math.floor(current));
        requestAnimationFrame(tick);
      } else {
        setCount(end);
      }
    };
    requestAnimationFrame(tick);
  }, [isInView, end]);

  return (
    <Typography
      ref={ref}
      variant="h3"
      sx={{ fontWeight: 600, color: "#8000FF", fontSize: { xs: "1.8rem", md: "2.2rem" } }}
    >
      {prefix}
      {count}
      {suffix}
    </Typography>
  );
}

export default function ResultsSection() {
  const stats = [
    { value: "+50", label: "Campañas exitosas" },
    { value: "+90%", label: "Clientes satisfechos" },
    { value: "+10", label: "Marcas aliadas" },
    { value: "10x", label: "Crecimiento promedio" },
  ];

  return (
    <Box
      sx={{
        position: "relative",
        backgroundColor: "#000",
        color: "#fff",
        py: { xs: 8, md: 5 },
        textAlign: "center",
        overflow: "hidden",
      }}
    >
      <Box
        sx={{
          position: "absolute",
          top: 0,
          left: 0,
          width: "100%",
          height: "100%",
          background: "linear-gradient(180deg,  rgba(0,0,0,0.85) 0%, rgba(128,0,255,0.25)  100%)",
          zIndex: 1,
        }}
      />
      <Box
        component={motion.div}
        initial={{ opacity: 0, y: 30 }}
        whileInView={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8 }}
        viewport={{ once: true }}
        sx={{
          position: "relative",
          zIndex: 2,
          maxWidth: "1100px",
          mx: "auto",
          px: 3,
        }}
      >
        <Typography
          component={motion.h3}
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
          sx={{
            fontWeight: 600,
            color: "#8000FF",
            mb: 3,
            fontSize: { xs: "1.5rem", md: "2rem" },
          }}
        >
          Medimos el éxito en resultados, no en promesas.
        </Typography>
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: {
              xs: "1fr 1fr",
              sm: "repeat(2, 1fr)",
              md: "repeat(4, 1fr)",
            },
            gap: { xs: 3, md: 6 },
            justifyContent: "center",
            mt: 6,
          }}
        >
          {stats.map((stat, index) => (
            <Box
              key={index}
              component={motion.div}
              whileHover={{ scale: 1.08 }}
              transition={{ type: "spring", stiffness: 200 }}
              sx={{
                backgroundColor: "rgba(255,255,255,0.05)",
                borderRadius: 4,
                py: { xs: 3, md: 4 },
                boxShadow: "0 0 20px rgba(128,0,255,0.2)",
                border: "1px solid rgba(128,0,255,0.3)",
              }}
            >
              <Counter target={stat.value} />
              <Typography
                variant="body1"
                sx={{
                  color: "#fff",
                  fontSize: { xs: "0.9rem", md: "1rem" },
                  mt: 1,
                }}
              >
                {stat.label}
              </Typography>
            </Box>
          ))}
        </Box>
      </Box>
    </Box>
  );
}
